import { useDropzone, FileRejection } from "react-dropzone";
import { FaLock } from "react-icons/fa";
import { RiUploadCloud2Line, RiFolderOpenLine } from "react-icons/ri";
import { FileWithPath } from "./groupByMatCode";

export const MAX_FILE_SIZE = 20_000_000; // 20 MB
export const ACCEPTED_IMAGE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

// Keeps S3 keys clean: filenames must be english/ascii only. Rejected files are
// surfaced with this reason so the user can rename and retry.
function nameValidator(file: File) {
  if (/\s/.test(file.name)) {
    return {
      code: "name-has-whitespace",
      message: "ชื่อไฟล์ต้องไม่มีช่องว่าง (space)",
    };
  }
  const english = /^[a-zA-Z0-9._-]+$/;
  if (!english.test(file.name)) {
    return {
      code: "name-not-in-english",
      message: "ชื่อไฟล์ต้องเป็นภาษาอังกฤษเท่านั้น",
    };
  }
  return null;
}

function PhotoDropZone({
  onFilesAdded,
  disabled,
}: {
  onFilesAdded: (accepted: FileWithPath[], rejections: FileRejection[]) => void;
  disabled: boolean;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled,
    accept: ACCEPTED_IMAGE_TYPES,
    maxSize: MAX_FILE_SIZE,
    validator: nameValidator,
    // Use the classic <input> path so `webkitdirectory` works for the browse
    // fallback. Drag-and-drop of folders is handled by getFilesFromEvent
    // regardless of this flag, so multi-folder drop still works.
    useFsAccessApi: false,
    onDrop: (accepted, rejections) => {
      onFilesAdded(accepted as FileWithPath[], rejections);
    },
  });

  return (
    <div
      {...getRootProps({
        className: `dropzone group relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-6 text-center transition-all duration-200 ${
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50"
            : isDragActive
            ? "scale-[1.01] border-blue-500 bg-blue-50 shadow-md"
            : "cursor-pointer border-gray-300 bg-gray-50/60 hover:border-blue-400 hover:bg-blue-50/50"
        }`,
      })}
    >
      {/* webkitdirectory makes the browse picker select one folder per click */}
      <input
        {...getInputProps()}
        {...({ webkitdirectory: "", directory: "" } as any)}
      />
      {disabled ? (
        <>
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-200">
            <FaLock size={26} className="fill-gray-500" />
          </div>
          <span className="font-medium text-gray-600">
            กำลังอัปโหลด… (ล็อกอยู่)
          </span>
        </>
      ) : (
        <>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              isDragActive
                ? "bg-blue-500 text-white"
                : "bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white"
            }`}
          >
            <RiUploadCloud2Line size={20} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-700">
              {isDragActive ? "วางเพื่อเพิ่มโฟลเดอร์" : "ลากโฟลเดอร์มาวางที่นี่"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              ตั้งชื่อโฟลเดอร์เป็น{" "}
              <span className="font-medium text-gray-600">Mat Code</span>{" "}
              แล้วใส่รูปไว้ข้างใน · วางพร้อมกันหลายโฟลเดอร์ได้
            </p>
          </div>

          {/* Folder-structure example */}
          <figure>
            <img
              src="/upload_matcode_demo.svg"
              alt="ตัวอย่างโครงสร้างโฟลเดอร์: ตั้งชื่อโฟลเดอร์เป็น Mat Code แล้ววางรูปไว้ข้างใน"
              className="w-96 rounded-lg border border-gray-200 bg-white"
            />
            <figcaption className="mt-1 text-[11px] text-gray-400">
              ตัวอย่างโครงสร้างโฟลเดอร์
            </figcaption>
          </figure>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors group-hover:border-blue-400 group-hover:text-blue-600">
            <RiFolderOpenLine size={15} /> หรือคลิกเพื่อเลือกโฟลเดอร์
          </span>
          <p className="text-xs text-gray-400">
            รองรับ .jpg .jpeg .png · ไม่เกิน 20&nbsp;MB ต่อไฟล์
          </p>
        </>
      )}
    </div>
  );
}

export default PhotoDropZone;
